import { useState } from "react";
import { Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Inline two-step delete button. Shows a small Trash icon; on first
 * click asks "Delete?" with Yes / No. On confirm, calls `onDelete()`
 * (an async fn). Renders success/failure toasts automatically.
 *
 * Props:
 *   - onDelete: async () => any   (throw to show error)
 *   - label:    string             — the entity name for the toast
 *   - testId:   string             — data-testid prefix
 *   - size:     number             — icon size (default 12)
 *   - tone:     "row" | "card"     — layout hint (default "row")
 *   - hoverOnly: boolean           — hide icon until parent hover
 */
export default function DeleteButton({
  onDelete,
  label = "Item",
  testId = "delete",
  size = 12,
  tone = "row",
  hoverOnly = false,
  successMessage,
  className = "",
}) {
  const [state, setState] = useState("idle"); // idle | confirm | busy

  const run = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setState("busy");
    try {
      await onDelete();
      toast.success(successMessage || `${label} deleted`);
      setState("idle");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || `Failed to delete ${label}`);
      setState("idle");
    }
  };

  if (state === "confirm" || state === "busy") {
    return (
      <div
        className={`flex items-center gap-1 shrink-0 ${className}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        data-testid={`${testId}-confirm-bar`}
      >
        <span className="text-[10px] uppercase tracking-widest text-amber-400 mr-1 whitespace-nowrap">Delete?</span>
        <button
          type="button"
          onClick={run}
          disabled={state === "busy"}
          className="text-[10px] px-2 py-1 border border-red-700 bg-red-900/30 text-red-300 hover:bg-red-900/60 uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-50"
          data-testid={`${testId}-yes`}
        >
          {state === "busy" ? <Loader2 size={10} className="animate-spin" /> : <Trash2 size={10} />} Yes
        </button>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setState("idle"); }}
          className="text-[10px] px-2 py-1 border border-stone-700 text-stone-400 hover:text-stone-200 uppercase tracking-widest"
          data-testid={`${testId}-no`}
        >
          No
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setState("confirm"); }}
      className={`p-1.5 border border-stone-800 text-stone-500 hover:text-red-400 hover:border-red-700 shrink-0 transition-colors ${
        hoverOnly ? "opacity-0 group-hover:opacity-100" : ""
      } ${tone === "card" ? "" : ""} ${className}`}
      title={`Delete ${label}`}
      data-testid={testId}
      aria-label={`Delete ${label}`}
    >
      <Trash2 size={size} />
    </button>
  );
}
